import { useState, useCallback, useRef, useEffect } from 'react';
import { vibrateAlert } from './useChatNotifications';
import { toast } from 'sonner';

var API = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
}

// Generate ringtone as WAV blob URL
function generateRingtoneWav(type) {
  var sampleRate = 44100;
  var duration = 2;
  var samples = sampleRate * duration;
  var data = new Float32Array(samples);

  if (type === 'gentle') {
    for (var i = 0; i < samples; i++) { var t = i / sampleRate; data[i] = Math.exp(-t * 2) * 0.4 * (Math.sin(2 * Math.PI * 523.25 * t) + 0.5 * Math.sin(2 * Math.PI * 659.25 * t)); }
  } else if (type === 'urgent') {
    for (var j = 0; j < samples; j++) { var t2 = j / sampleRate; data[j] = (Math.floor(t2 * 6) % 2 === 0 ? 1 : 0) * 0.35 * Math.sin(2 * Math.PI * 880 * t2); }
  } else if (type === 'minimal') {
    for (var k = 0; k < samples; k++) { var t3 = k / sampleRate; data[k] = (t3 < 0.15 || (t3 > 0.4 && t3 < 0.55) ? 1 : 0) * 0.3 * Math.sin(2 * Math.PI * 1000 * t3); }
  } else {
    for (var m = 0; m < samples; m++) { var t4 = m / sampleRate; data[m] = Math.sin(2 * Math.PI * 440 * t4) * (1 + 0.3 * Math.sin(2 * Math.PI * 20 * t4)) * (t4 < 0.8 ? 1 : Math.max(0, 1 - (t4 - 0.8) * 3)) * 0.35; }
  }

  var numCh = 1, bps = 16, byteRate = sampleRate * numCh * (bps / 8), blockAlign = numCh * (bps / 8), dataSize = samples * blockAlign;
  var buffer = new ArrayBuffer(44 + dataSize);
  var view = new DataView(buffer);
  var ws = function(o, s) { for (var x = 0; x < s.length; x++) view.setUint8(o + x, s.charCodeAt(x)); };
  ws(0, 'RIFF'); view.setUint32(4, 36 + dataSize, true); ws(8, 'WAVE'); ws(12, 'fmt ');
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, numCh, true);
  view.setUint32(24, sampleRate, true); view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true); view.setUint16(34, bps, true);
  ws(36, 'data'); view.setUint32(40, dataSize, true);
  for (var n = 0; n < samples; n++) { var s = Math.max(-1, Math.min(1, data[n])); view.setInt16(44 + n * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true); }
  return URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
}

var DEFAULT_RINGTONE = '/sounds/voice_call_ring.mp3';
var VIDEO_RINGTONE = '/sounds/video_call_ring.mp3';
var CALLING_TONE = '/sounds/notification.mp3';

/**
 * useCallManager - Server-authoritative call state machine
 * 
 * The DB is the single source of truth. REST polling (every 2s) is the PRIMARY
 * mechanism for detecting state changes. WebSocket events are a fast-path optimization.
 */
export function useCallManager(sendWsMessage, onRestSignal) {
  // Single state object for the entire call lifecycle
  var initialState = {
    phase: 'idle',  // idle | outgoing_ringing | incoming_ringing | connecting | connected | ended
    callId: null,
    callType: null,
    isInitiator: false,
    callerName: null,
    callerId: null,
    conversationId: null,
    recipientId: null,
    participants: [],   // [{user_id, name}] everyone currently in the call
    peersToOffer: [],   // set for the joiner: peers we must send WebRTC offers to
    mediaMode: null,    // 'livekit' (SFU) | 'mesh' (legacy P2P) — decided by the server per call
  };

  var callStateRef = useRef(initialState);
  var _s = useState(initialState);
  var callState = _s[0];
  var _setCallState = _s[1];

  // Sync ref with state for async callbacks
  var setCallState = useCallback(function(newState) {
    if (typeof newState === 'function') {
      _setCallState(function(prev) {
        var next = newState(prev);
        callStateRef.current = next;
        return next;
      });
    } else {
      callStateRef.current = newState;
      _setCallState(newState);
    }
  }, []);

  // Background sleep mode must never pause timers during a call (native call screen keeps the page hidden)
  useEffect(function() { window.__bbcCallActive = callState.phase !== 'idle'; }, [callState.phase]);

  // Ringtone refs
  var incomingRingtoneRef = useRef(null);
  var videoRingtoneRef = useRef(null);
  var outgoingToneRef = useRef(null);
  var ringtoneSettingsLoaded = useRef(false);
  var pollIntervalRef = useRef(null);
  var connectingTimeoutRef = useRef(null);

  // ==================== RINGTONE MANAGEMENT ====================

  var loadRingtoneSettings = useCallback(function() {
    var token = getToken();
    if (!token || ringtoneSettingsLoaded.current) return;

    // Load notification preferences for sound settings
    fetch(API + '/api/notifications/preferences', {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) {
        if (data && data.preferences) {
          ringtoneSettingsLoaded.current = true;
          var prefs = data.preferences;
          var voiceSound = prefs.voice_call_sound || 'voice_call_ring';
          var videoSound = prefs.video_call_sound || 'video_call_ring';

          // Set voice call ringtone
          if (voiceSound !== 'none') {
            incomingRingtoneRef.current = new Audio('/sounds/' + voiceSound + '.mp3');
            incomingRingtoneRef.current.loop = true;
            incomingRingtoneRef.current.volume = 0.7;
            incomingRingtoneRef.current.load();
          }
          // Set video call ringtone
          if (videoSound !== 'none') {
            videoRingtoneRef.current = new Audio('/sounds/' + videoSound + '.mp3');
            videoRingtoneRef.current.loop = true;
            videoRingtoneRef.current.volume = 0.7;
            videoRingtoneRef.current.load();
          }
        }
      })
      .catch(function() {});

    // Also check chat ringtone settings for custom uploads
    fetch(API + '/api/chat/ringtone-settings', {
      headers: { Authorization: 'Bearer ' + token }
    })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(settings) {
        if (settings && settings.incoming_ringtone === 'custom' && settings.custom_ringtone_url) {
          incomingRingtoneRef.current = new Audio(API + settings.custom_ringtone_url);
          incomingRingtoneRef.current.loop = true;
          incomingRingtoneRef.current.volume = 0.7;
          incomingRingtoneRef.current.load();
        }
      })
      .catch(function() {});

    // Outgoing tone
    outgoingToneRef.current = new Audio(CALLING_TONE);
    outgoingToneRef.current.loop = true;
    outgoingToneRef.current.volume = 0.5;
    outgoingToneRef.current.load();
  }, []);

  // Initialize defaults and load settings on mount
  useEffect(function() {
    incomingRingtoneRef.current = new Audio(DEFAULT_RINGTONE);
    incomingRingtoneRef.current.loop = true;
    incomingRingtoneRef.current.volume = 0.7;

    videoRingtoneRef.current = new Audio(VIDEO_RINGTONE);
    videoRingtoneRef.current.loop = true;
    videoRingtoneRef.current.volume = 0.7;

    outgoingToneRef.current = new Audio(CALLING_TONE);
    outgoingToneRef.current.loop = true;
    outgoingToneRef.current.volume = 0.5;

    loadRingtoneSettings();

    // Listen for settings changes from RingtoneSettings or NotificationSettings
    var handler = function() { ringtoneSettingsLoaded.current = false; loadRingtoneSettings(); };
    window.addEventListener('ringtone-settings-changed', handler);
    window.addEventListener('notification-sounds-changed', handler);
    return function() {
      window.removeEventListener('ringtone-settings-changed', handler);
      window.removeEventListener('notification-sounds-changed', handler);
    };
  }, [loadRingtoneSettings]);

  var playRingtone = useCallback(function(type, callType) {
    // Eagerly reload settings if not loaded
    if (!ringtoneSettingsLoaded.current) loadRingtoneSettings();

    try {
      if (type === 'incoming') {
        // Vibrate with the in-app ring when the user has vibration on in Notification Settings
        try {
          var prefsRaw = localStorage.getItem('chat_notification_prefs');
          var prefs = prefsRaw ? JSON.parse(prefsRaw) : null;
          if (!prefs || prefs.vibrate_enabled !== false) vibrateAlert([0, 500, 200, 500, 200, 500]);
        } catch (e) { /* noop */ }
        // Use video ringtone for video calls, voice ringtone for voice calls
        var ringtoneRef = (callType === 'video' && videoRingtoneRef.current) ? videoRingtoneRef : incomingRingtoneRef;
        if (ringtoneRef.current) {
          ringtoneRef.current.currentTime = 0;
          ringtoneRef.current.play().catch(function() {});
        }
      } else if (type === 'outgoing' && outgoingToneRef.current) {
        outgoingToneRef.current.currentTime = 0;
        outgoingToneRef.current.play().catch(function() {});
      }
    } catch (e) { console.error('Ringtone play error:', e); }
  }, [loadRingtoneSettings]);

  var stopAllRingtones = useCallback(function() {
    try {
      if (incomingRingtoneRef.current) { incomingRingtoneRef.current.pause(); incomingRingtoneRef.current.currentTime = 0; }
      if (videoRingtoneRef.current) { videoRingtoneRef.current.pause(); videoRingtoneRef.current.currentTime = 0; }
      if (outgoingToneRef.current) { outgoingToneRef.current.pause(); outgoingToneRef.current.currentTime = 0; }
    } catch (e) {}
  }, []);

  // ==================== REST POLLING (Primary State Sync) ====================

  var stopPoll = useCallback(function() {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
  }, []);

  var startPoll = useCallback(function(callId) {
    stopPoll();
    if (!callId || callId === 'pending') return;

    pollIntervalRef.current = setInterval(function() {
      var token = getToken();
      if (!token) return;

      var currentPhase = callStateRef.current.phase;
      if (currentPhase === 'idle' || currentPhase === 'ended') {
        stopPoll();
        return;
      }

      fetch(API + '/api/calls/' + callId, {
        headers: { Authorization: 'Bearer ' + token }
      })
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(data) {
          if (!data) return;
          var cp = callStateRef.current.phase;
          if (cp === 'idle' || cp === 'ended') return;
          if (data.media_mode && callStateRef.current.mediaMode && data.media_mode !== callStateRef.current.mediaMode) {
            console.log('[CallManager] media mode changed by peer →', data.media_mode);
            setCallState(function(prev) { return Object.assign({}, prev, { mediaMode: data.media_mode }); });
          }
          if (data.signals && data.signals.length && onRestSignal) data.signals.forEach(function(sig) { try { onRestSignal(sig); } catch (e) { /* noop */ } });

          if (data.status === 'ended' || data.status === 'rejected' || data.status === 'missed') {
            console.log('[CallManager] REST poll: call ' + data.status + ' — ending');
            stopAllRingtones();
            stopPoll();
            setCallState({ phase: 'idle', callId: null, callType: null, isInitiator: false, callerName: null, callerId: null, conversationId: null, recipientId: null, participants: [], peersToOffer: [] });
          } else if (data.status === 'ongoing' && cp === 'outgoing_ringing') {
            // Call was accepted (we might have missed the WS event)
            console.log('[CallManager] REST poll: call now ongoing — transitioning to connecting');
            stopAllRingtones();
            // Don't transition here — wait for call_accepted WS which has accepted_by info
          }
        })
        .catch(function() {});
    }, 4000);
  }, [stopPoll, stopAllRingtones, setCallState]);

  // Auto-timeout: if stuck in connecting/ringing for too long, auto-end.
  // Two distinct windows so a slow notification tap doesn't kill an
  // outgoing call before the recipient even gets the push:
  //   - outgoing_ringing: 120 s (typical phone ringing duration; gives
  //     the recipient time to wake the device, find the notification
  //     and cold-start the app)
  //   - connecting: 60 s (post-accept WebRTC handshake — should be
  //     fast; if it's not, ICE failed and the call should be ended)
  // The previous 60 s outgoing-ringing window was the user's bug:
  // caller's timeout fired BEFORE the recipient could even tap the
  // push, so by the time the SPA opened and hit /api/calls/{id} the
  // status was already "ended" — surfaced as the "Missed call from X"
  // toast (screenshot evidence).
  var startConnectingTimeout = useCallback(function(callId, mode) {
    if (connectingTimeoutRef.current) clearTimeout(connectingTimeoutRef.current);
    var ringSec = Number(window.__bbcNoAnswerSeconds) || 60; // admin setting (Settings → Ring time before "No answer")
    var ms = mode === 'connecting' ? 60000 : ringSec * 1000 + 15000; // ringing: admin ring time + push wake-up margin
    connectingTimeoutRef.current = setTimeout(function() {
      var cs = callStateRef.current;
      if (cs.phase === 'connecting' || cs.phase === 'outgoing_ringing') {
        console.log('[CallManager] Auto-timeout: call stuck in ' + cs.phase + ', ending');
        stopAllRingtones();
        stopPoll();
        if (callId && callId !== 'pending') {
          var token = getToken();
          if (token) {
            fetch(API + '/api/calls/' + callId + '/end', {
              method: 'POST',
              headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
              body: JSON.stringify({ reason: cs.phase === 'connecting' ? 'network' : 'no_answer' })
            }).catch(function() {});
          }
        }
        setCallState({ phase: 'idle', callId: null, callType: null, isInitiator: false, callerName: null, callerId: null, conversationId: null, recipientId: null, participants: [], peersToOffer: [] });
      }
    }, ms);
  }, [stopAllRingtones, stopPoll, setCallState]);

  var clearConnectingTimeout = useCallback(function() {
    if (connectingTimeoutRef.current) { clearTimeout(connectingTimeoutRef.current); connectingTimeoutRef.current = null; }
  }, []);

  // ==================== CALL ACTIONS ====================

  var initiateCall = useCallback(function(conversationId, callType) {
    if (callStateRef.current.phase !== 'idle') return;
    
    sendWsMessage({
      type: 'call_initiate',
      conversation_id: conversationId,
      call_type: callType || 'video'
    });

    setCallState({
      phase: 'outgoing_ringing',
      callId: null, // Will be set by call_initiated WS event
      callType: callType || 'video',
      isInitiator: true,
      callerName: null,
      callerId: null,
      conversationId: conversationId,
      recipientId: null,
      participants: [],
      peersToOffer: [],
      mediaMode: null,
    });
    playRingtone('outgoing');
    startConnectingTimeout(null); // Will be updated when call_initiated arrives with real ID
  }, [sendWsMessage, setCallState, playRingtone, startConnectingTimeout]);

  var acceptCall = useCallback(function() {
    var cs = callStateRef.current;
    if (cs.phase !== 'incoming_ringing' || !cs.callId) return;

    stopAllRingtones();
    // Try WS first; if not OPEN, hit REST `/accept` so backend can
    // broadcast `call_accepted` to the caller via THEIR WS.  Without
    // this, accepts during cold-start (WS still reconnecting) are
    // silently dropped, the caller's 60s timeout fires, and the call
    // ends on the caller's app — exactly the bug the user reported.
    var sentViaWs = sendWsMessage({ type: 'call_accept', call_id: cs.callId });
    if (sentViaWs && typeof sentViaWs.then === 'function') {
      sentViaWs = sentViaWs.then(function(ok) {
        if (!ok) {
          // Async fallback when sendWsMessage returns false from a Promise
          var t = (typeof getToken === 'function') ? getToken() : null;
          if (t) {
            fetch(API + '/api/calls/' + cs.callId + '/accept', {
              method: 'POST',
              headers: { 'Authorization': 'Bearer ' + t },
            }).catch(function() {});
          }
        }
        return ok;
      });
    } else if (!sentViaWs) {
      // Synchronous false — WS not OPEN.  REST fallback.
      var t = (typeof getToken === 'function') ? getToken() : null;
      if (t) {
        fetch(API + '/api/calls/' + cs.callId + '/accept', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + t },
        }).catch(function() {});
      }
    }
    setCallState(function(prev) { return Object.assign({}, prev, { phase: 'connecting' }); });
    // Re-arm with the post-accept window (60 s for the WebRTC handshake).
    clearConnectingTimeout();
    startConnectingTimeout(cs.callId, 'connecting');
  }, [sendWsMessage, stopAllRingtones, setCallState, clearConnectingTimeout, startConnectingTimeout]);

  var rejectCall = useCallback(function() {
    var cs = callStateRef.current;
    if (!cs.callId) return;

    stopAllRingtones();
    stopPoll();
    sendWsMessage({ type: 'call_reject', call_id: cs.callId });
    setCallState({ phase: 'idle', callId: null, callType: null, isInitiator: false, callerName: null, callerId: null, conversationId: null, recipientId: null, participants: [], peersToOffer: [] });
  }, [sendWsMessage, stopAllRingtones, stopPoll, setCallState]);

  var endCall = useCallback(function(reason, forAll) {
    var cs = callStateRef.current;
    var cid = cs.callId;

    stopAllRingtones();
    stopPoll();
    clearConnectingTimeout();

    if (cid && cid !== 'pending') {
      var rsn = (typeof reason === 'string') ? reason : (cs.phase === 'connected' ? 'hung_up' : (cs.isInitiator ? 'cancelled' : 'hung_up'));
      // Send via BOTH WS and REST for maximum reliability
      sendWsMessage({ type: 'call_end', call_id: cid, reason: rsn, for_all: !!forAll });

      var token = getToken();
      if (token) {
        fetch(API + '/api/calls/' + cid + '/end', {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: rsn, for_all: !!forAll })
        }).catch(function() {});
      }
    }

    setCallState({ phase: 'idle', callId: null, callType: null, isInitiator: false, callerName: null, callerId: null, conversationId: null, recipientId: null, participants: [], peersToOffer: [] });
  }, [sendWsMessage, stopAllRingtones, stopPoll, clearConnectingTimeout, setCallState]);

  // First peer connection is up: the call is live and runs until someone hangs up.
  var markConnected = useCallback(function() {
    clearConnectingTimeout();
    setCallState(function(prev) {
      if (prev.phase !== 'connecting' && prev.phase !== 'outgoing_ringing') return prev;
      return Object.assign({}, prev, { phase: 'connected' });
    });
  }, [clearConnectingTimeout, setCallState]);

  var inviteToCall = useCallback(function(userIds) {
    var cs = callStateRef.current;
    if (!cs.callId || cs.callId === 'pending' || !userIds || !userIds.length) return;
    sendWsMessage({ type: 'call_invite', call_id: cs.callId, user_ids: userIds });
  }, [sendWsMessage]);

  var clearPeersToOffer = useCallback(function() {
    setCallState(function(prev) { return prev.peersToOffer && prev.peersToOffer.length ? Object.assign({}, prev, { peersToOffer: [] }) : prev; });
  }, [setCallState]);

  // ==================== WEBRTC SIGNALING ====================

  var sendWebRTCSignal = useCallback(function(signalType, callId, recipientId, payload) {
    var viaRest = function() {
      var token = getToken(); if (!token || !callId || callId === 'pending') return;
      fetch(API + '/api/calls/' + callId + '/signal', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ signal_type: signalType, recipient_id: recipientId, payload: payload }) }).catch(function() {});
    };
    Promise.resolve(sendWsMessage({
      type: 'webrtc_signal',
      signal_type: signalType,
      call_id: callId,
      recipient_id: recipientId,
      payload: payload
    })).then(function(ok) { if (ok === false) viaRest(); }).catch(viaRest);
    // Offers/answers are critical for reconnecting on a weak network: also queue them server-side so the
    // peer picks them up on its next poll even if its socket is down (duplicates are harmless).
    if (signalType === 'offer' || signalType === 'answer') viaRest();
  }, [sendWsMessage]);

  // ==================== WS EVENT HANDLERS (Fast Path) ====================

  var handleWsEvent = useCallback(function(data) {
    var msgType = data.type;
    console.log('[CallManager] WS event:', msgType, data);

    if (msgType === 'call_ringing') {
      // The receiver's phone confirmed it is ringing (native push handled) — proof the push woke it
      if (callStateRef.current.callId === data.call_id && callStateRef.current.phase === 'outgoing_ringing') {
        toast.success(`Ringing on ${data.user_name ? data.user_name.split(' ')[0] : 'their'} phone` + (data.latency_s != null ? ` (${data.latency_s}s)` : ''), { id: 'ring-ack-' + data.call_id, duration: 4000 });
      }
      return;
    }

    if (msgType === 'call_busy') {
      var csb = callStateRef.current;
      if (!data.call_id && data.message) { toast.info(data.message, { duration: 8000 }); return; } // pre-ring notice: some invitees skipped (already in a call)
      if (csb.callId === data.call_id && csb.phase === 'outgoing_ringing') {
        var note = (data.user_name ? data.user_name.split(' ')[0] + ' is' : 'Receiver') + ' on another call';
        setCallState(function(prev) { return { ...prev, statusNote: note }; });
        toast.error(note, { id: 'busy-' + data.call_id, description: 'Their phone is busy with another call. We keep ringing — they can pick up when they finish.', duration: 15000 });
        try { var nb = window.AndroidBridge; if (nb && typeof nb.setNativeCallStatus === 'function') nb.setNativeCallStatus('Receiver on another call'); } catch (e) { /* older app */ }
      }
      return;
    }
    if (msgType === 'ring_unacked') {
      var cs0 = callStateRef.current;
      if (cs0.callId === data.call_id && cs0.phase === 'outgoing_ringing') {
        var who = data.user_name ? data.user_name.split(' ')[0] : 'Their';
        toast.warning(`${who}'s phone has not picked up the ring yet` + (data.online ? ' (app open in background?)' : ' (app closed / battery-restricted)') + ' — ringing again…', { id: 'ring-unacked-' + data.call_id, duration: 8000 });
      }
      return;
    }
    if (msgType === 'call_transcription') {
      window.dispatchEvent(new CustomEvent('bbc:call-transcription', { detail: data }));
      return;
    }

    if (msgType === 'incoming_call') {
      if (callStateRef.current.phase !== 'idle') {
        // Already in a call: tell the caller we are busy instead of letting the second ring interfere
        if (callStateRef.current.callId !== data.call_id) {
          fetch(API + '/api/calls/' + data.call_id + '/busy', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') }, body: JSON.stringify({ reason: 'in_call' }) }).catch(function() {});
          try { var nbz = window.AndroidBridge; if (nbz && typeof nbz.dismissIncomingCall === 'function') nbz.dismissIncomingCall(); } catch (e) { /* older app */ }
        }
        return;
      }
      setCallState({
        phase: 'incoming_ringing',
        callId: data.call_id,
        callType: data.call_type || 'video',
        isInitiator: !!data.is_host, // scheduled auto-ring: the person who scheduled it keeps the host tools
        callerName: data.caller_name,
        callerId: data.caller_id,
        conversationId: data.conversation_id,
        recipientId: null,
        participants: [],
        peersToOffer: [],
        mediaMode: data.media_mode || null,
      });
      playRingtone('incoming', data.call_type || 'video');
      startPoll(data.call_id);
      startConnectingTimeout(data.call_id);
    }

    else if (msgType === 'call_initiated') {
      // Confirmation from server with call_id
      if (data.no_answer_seconds) window.__bbcNoAnswerSeconds = data.no_answer_seconds; // admin ring time saved after login applies to this call too
      setCallState(function(prev) {
        if (prev.phase !== 'outgoing_ringing') return prev;
        var newState = Object.assign({}, prev, { callId: data.call_id, recipientId: data.recipients ? data.recipients[0] : null, mediaMode: data.media_mode || prev.mediaMode || null });
        return newState;
      });
      startPoll(data.call_id);
      startConnectingTimeout(data.call_id);
    }

    else if (msgType === 'call_accepted') {
      // Someone joined.  The JOINER sends us the WebRTC offer, so we just
      // record the participant and (if we were still ringing) move to connecting.
      stopAllRingtones();
      setCallState(function(prev) {
        if (prev.phase === 'idle' || prev.phase === 'incoming_ringing') return prev;
        var parts = data.participants || (prev.participants || []).concat([{ user_id: data.accepted_by, name: data.accepted_by_name || '' }]);
        return Object.assign({}, prev, {
          statusNote: null,
          phase: prev.phase === 'outgoing_ringing' ? 'connecting' : prev.phase,
          recipientId: prev.recipientId || data.accepted_by,
          participants: parts,
        });
      });
      if (callStateRef.current.phase !== 'connected') startConnectingTimeout(callStateRef.current.callId, 'connecting');
    }

    else if (msgType === 'call_participants') {
      // We just accepted: connect to everyone already in the call (we initiate).
      setCallState(function(prev) {
        return Object.assign({}, prev, {
          participants: data.all_participants || prev.participants || [],
          peersToOffer: data.participants || [],
        });
      });
    }

    else if (msgType === 'call_invited') {
      // Extra people are being rung — nothing to do until they accept.
    }

    else if (msgType === 'participant_left') {
      setCallState(function(prev) {
        var parts = (prev.participants || []).filter(function(p) { return p.user_id !== data.user_id; });
        return Object.assign({}, prev, { participants: parts, leftPeer: data.user_id, leftAt: Date.now() });
      });
    }

    else if (msgType === 'call_failed') {
      stopAllRingtones();
      stopPoll();
      clearConnectingTimeout();
      toast.error(data.message || 'Receiver not logged in');
      // Offline callback: offer to leave a "call me back" note that pops up at their next login
      var rid = callStateRef.current && callStateRef.current.recipientId;
      if (rid && data.reason === 'not_logged_in') {
        window.dispatchEvent(new CustomEvent('bbc:offer-callback-note', { detail: { recipient_id: rid, conversation_id: callStateRef.current.conversationId, call_type: callStateRef.current.callType || 'audio', name: (data.message || '').split(' is not logged in')[0] } }));
      }
      setCallState({ phase: 'idle', callId: null, callType: null, isInitiator: false, callerName: null, callerId: null, conversationId: null, recipientId: null, participants: [], peersToOffer: [] });
    }

    else if (msgType === 'call_rejected' && data.call_still_active) {
      // One invitee declined but the call carries on with the others.
    }

    else if (msgType === 'call_rejected') {
      stopAllRingtones();
      stopPoll();
      clearConnectingTimeout();
      setCallState({ phase: 'idle', callId: null, callType: null, isInitiator: false, callerName: null, callerId: null, conversationId: null, recipientId: null, participants: [], peersToOffer: [] });
    }

    else if (msgType === 'call_ended') {
      if (data.removed) toast.info(`${data.by_name || 'The host'} removed you from the call`);
      stopAllRingtones();
      stopPoll();
      clearConnectingTimeout();
      setCallState({ phase: 'idle', callId: null, callType: null, isInitiator: false, callerName: null, callerId: null, conversationId: null, recipientId: null, participants: [], peersToOffer: [] });
    }
  }, [setCallState, playRingtone, startPoll, stopAllRingtones, stopPoll, startConnectingTimeout, clearConnectingTimeout]);

  // Cleanup on unmount
  useEffect(function() {
    return function() {
      stopPoll();
      stopAllRingtones();
      clearConnectingTimeout();
    };
  }, [stopPoll, stopAllRingtones, clearConnectingTimeout]);

  // Beacon on page close
  useEffect(function() {
    var onUnload = function() {
      var cs = callStateRef.current;
      // A ringing RECIPIENT reloading (e.g. push-notification tap that
      // hard-navigates) must not hang up on the caller — the call is
      // re-surfaced after reload via WS replay / pending-incoming poll.
      if (cs.callId && cs.phase !== 'idle' && cs.phase !== 'incoming_ringing') {
        var token = getToken();
        if (token && navigator.sendBeacon) {
          navigator.sendBeacon(API + '/api/calls/' + cs.callId + '/end-beacon?token=' + token, '{}');
        }
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return function() { window.removeEventListener('beforeunload', onUnload); };
  }, []);

  var setMediaMode = useCallback(function(mode) {
    setCallState(function(prev) { return prev.mediaMode === mode ? prev : Object.assign({}, prev, { mediaMode: mode }); });
  }, [setCallState]);

  // BBC Android app: native incoming-call screen (Accept/Decline) + session hand-over for native REST calls.
  var pendingNativeAcceptRef = useRef(null);
  useEffect(function() {
    var bridge = window.AndroidBridge;
    if (!bridge) return undefined;
    try { if (typeof bridge.setSession === 'function') bridge.setSession(getToken() || '', API); } catch (e) { /* older app */ }
    try { var parked = typeof bridge.takePendingAcceptCall === 'function' ? bridge.takePendingAcceptCall() : ''; if (parked) pendingNativeAcceptRef.current = parked; } catch (e) { /* noop */ }
    var onAccept = function(e) { pendingNativeAcceptRef.current = (e.detail && e.detail.call_id) || null; if (pendingNativeAcceptRef.current) tryAcceptPending(); };
    var onReject = function(e) { var cs = callStateRef.current; if (e.detail && e.detail.call_id === cs.callId && cs.phase === 'incoming_ringing') rejectCall(); };
    window.addEventListener('bbc:accept-call', onAccept);
    window.addEventListener('bbc:reject-call', onReject);
    return function() { window.removeEventListener('bbc:accept-call', onAccept); window.removeEventListener('bbc:reject-call', onReject); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var tryAcceptPending = useCallback(function() {
    var id = pendingNativeAcceptRef.current; var cs = callStateRef.current;
    if (id && cs.phase === 'incoming_ringing' && cs.callId === id) { pendingNativeAcceptRef.current = null; acceptCall(); }
  }, [acceptCall]);
  useEffect(function() { tryAcceptPending(); }, [callState.phase, callState.callId, tryAcceptPending]);
  useEffect(function() {
    // when the native screen shows a call, the web dialog is redundant: tell native when the ring stops here
    var bridge = window.AndroidBridge;
    if (bridge && typeof bridge.endNativeCall === 'function' && callState.phase !== 'incoming_ringing') { try { if (typeof bridge.dismissIncomingCall === 'function') bridge.dismissIncomingCall(); } catch (e) { /* noop */ } }
  }, [callState.phase]);

  return {
    callState: callState,
    setMediaMode: setMediaMode,
    initiateCall: initiateCall,
    acceptCall: acceptCall,
    rejectCall: rejectCall,
    endCall: endCall,
    markConnected: markConnected,
    inviteToCall: inviteToCall,
    clearPeersToOffer: clearPeersToOffer,
    sendWebRTCSignal: sendWebRTCSignal,
    handleWsEvent: handleWsEvent,
    stopAllRingtones: stopAllRingtones,
  };
}
